import React, { useState, useRef, useContext } from "react";
import { Link, useNavigate } from "react-router-dom";
import Switch from "@mui/material/Switch";
import swal from "sweetalert2";

import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftInput from "components/SoftInput";
import SoftButton from "components/SoftButton";
import CoverLayout from "layouts/authentication/components/CoverLayout";
import curved9 from "assets/images/curved-images/login_img.jpg";
import AuthContext from "context/AuthContext";
import { setSecureItem, removeSecureItem } from "utils/SecureStorage";
import { api } from "../../../utils/Api";
import IconButton from "@mui/material/IconButton";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { resetPermissionCache } from "utils/permissionManager";

// ✅ Same normalizer used everywhere
const normalizeRoleName = (name) => {
  if (!name) return null;
  const lower = name.toLowerCase().trim();

  if (
    lower === "super admin" ||
    lower === "super_admin" ||
    lower === "superadmin"
  )
    return "super_admin";
  if (lower === "admin") return "admin";
  if (lower === "manager") return "manager";
  if (lower === "staff") return "staff";
  if (lower === "tenant" || lower === "user") return "tenant";

  return null;
};

function SignIn() {
  const [rememberMe, setRememberMe] = useState(true);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [message, setMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const formRef = useRef();
  const navigate = useNavigate();
  const { login } = useContext(AuthContext);
  const [showPassword, setShowPassword] = useState(false);
  const [usernameError, setUsernameError] = useState(false);
  const [passwordError, setPasswordError] = useState(false);

  const handleTogglePassword = () => {
    setShowPassword((prev) => !prev);
  };

  const handleSetRememberMe = () => setRememberMe(!rememberMe);

  // ✅ Clear all previous auth data
  const clearPreviousAuth = () => {
    const keysToRemove = [
      "admin",
      "super_admin",
      "manager",
      "staffauthTokens",
      "authTokens",
      "accessToken",
      "refreshToken",
      "currentUser",
      "currentUserRole",
      "roles",
      "role_id",
      "accessControl",
      "entities",
    ];
    keysToRemove.forEach((key) => removeSecureItem(key));
  };

  const fetchAndStoreAccessData = async (organization_id) => {
    try {
      const [permissionsRes, entitiesRes] = await Promise.all([
        api.accessControl.getAccessControl({ organization_id }),
        api.accessControl.getEnumsEntity(),
      ]);

      // Store in secure storage
      setSecureItem("accessControl", permissionsRes);

      setSecureItem("entities", entitiesRes);
    } catch (error) {
    }
  };

  // ✅ DYNAMIC role resolution using fetched roles list
  const handleRoleBasedAuth = (userData, tokens, rolesList) => {
    clearPreviousAuth();

    const userRoleId = userData?.role_id;

    // Find the matching role from the API roles list
    const matchedRole = rolesList.find(
      (r) => r.id === userRoleId && r.active
    );

    const roleKey = matchedRole
      ? normalizeRoleName(matchedRole.name)
      : null;

    // No valid role → warning page
    if (!roleKey) {
      return {
        redirectPath: "/role-not-assigned",
        role: "unassigned",
      };
    }

    // ✅ Store common data
    setSecureItem("accessToken", tokens.access_token);
    setSecureItem("refreshToken", tokens.refresh_token);
    setSecureItem("currentUser", JSON.stringify(userData));
    // setSecureItem("currentUserRole", roleKey);
    setSecureItem("role_id", String(userRoleId)); // ← THIS WAS MISSING
    setSecureItem("roles", JSON.stringify(rolesList));

    const authData = JSON.stringify({
      ...tokens,
      user: userData,
    });

    let redirectPath = "/user/unit";

    // ✅ Set legacy flags based on DYNAMIC role key
    switch (roleKey) {
      case "super_admin":
        // setSecureItem("admin", "True");
        // setSecureItem("super_admin", "True");
        // setSecureItem("authTokens", authData);
        redirectPath = "/super-admin/organization";
        break;

      case "admin":
        // setSecureItem("admin", "True");
        // setSecureItem("authTokens", authData);
        redirectPath = "/admin/properties";
        break;

      case "manager":
        // setSecureItem("manager", "True");
        // setSecureItem("authTokens", authData);
        redirectPath = "/manager/properties";
        break;

      case "staff":
        // setSecureItem("staffauthTokens", authData);
        redirectPath = "/concierge/unit";
        break;

      case "tenant":
      default:
        // setSecureItem("authTokens", authData);
        redirectPath = "/user/properties";
        break;
    }

    return { redirectPath, role: roleKey };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setMessage("");

    try {
      // Detect if input is email
      const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(username);

      // Build payload dynamically
      const loginPayload = {
        password,
        ...(isEmail ? { email: username } : { username }),
      };

      const loginRes = await api.login.postLogin(loginPayload);
      const { access_token, refresh_token } = loginRes;

      if (!access_token) {
        throw new Error("Invalid response from server.");
      }

      // Temporarily store tokens so subsequent API calls work
      setSecureItem("accessToken", access_token);
      setSecureItem("refreshToken", refresh_token);

      // 🔹 STEP 2 — Fetch user + roles in parallel
      const [userRes, rolesRes] = await Promise.all([api.user.getUserSelf(), api.roles.getRoles()]);

      const rolesList = rolesRes?.data || rolesRes;

      // 🔹 STEP 3 — Dynamic role-based auth & storage
      const { redirectPath, role } = handleRoleBasedAuth(
        userRes,
        { access_token, refresh_token },
        rolesList
      );

      // 🔹 STEP 4 — Update AuthContext
      login({
        access_token,
        refresh_token,
        user: userRes,
      });

      // 🔹 STEP 5 — Fetch permissions & entities
      const userRoleId = userRes?.role_id;
      const organizationId = userRes?.organization_id;

      if (userRoleId !== 1 && userRoleId !== 2) {
        await fetchAndStoreAccessData(organizationId);
        resetPermissionCache();
      }

      // 🔹 STEP 6 — Success message
      swal.fire({
        title: "Login Successful!",
        text: `Welcome back, ${userRes?.name || username}!`,
        icon: "success",
        toast: true,
        timer: 5000,
        position: "bottom-right",
        showConfirmButton: false,
        showCloseButton: true,
      });

      // 🔹 STEP 7 — Navigate
      navigate(redirectPath, { replace: true });
    } catch (error) {
      const errorMessage =
        error?.response?.data?.detail ||
        error?.response?.data?.message ||
        error?.message ||
        "Login failed";

      setUsernameError(false);
      setPasswordError(false);

      const msg = errorMessage.toLowerCase();

      if (msg.includes("invalid email or username")) {
        setUsernameError(true);
      } else if (msg.includes("password")) {
        setPasswordError(true);
      } else {
        setUsernameError(true);
        setPasswordError(true);
      }

      swal.fire({
        title: "Login Failed",
        text: errorMessage,
        icon: "error",
        toast: true,
        timer: 5000,
        position: "bottom-right",
        showConfirmButton: false,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const isSubmitDisabled = isLoading || !username || !password;

  return (
    <CoverLayout
      title="Welcome back"
      description="Enter your Username and Password"
      image={curved9}
    >
      <SoftBox component="form" role="form" ref={formRef} onSubmit={handleSubmit}>
        <SoftBox mb={2}>
          <SoftBox mb={1} ml={0.5}>
            <SoftTypography component="label" variant="caption" fontWeight="bold">
              Email or Username
            </SoftTypography>
          </SoftBox>
          <SoftInput
            type="text"
            name="username"
            placeholder="Enter your username or email"
            value={username}
            onChange={(e) => {
              setUsername(e.target.value);
              setUsernameError(false);
            }}
            error={usernameError}
          />
        </SoftBox>

        <SoftBox mb={2}>
          <SoftBox mb={1} ml={0.5}>
            <SoftTypography component="label" variant="caption" fontWeight="bold">
              Password
            </SoftTypography>
          </SoftBox>
          {/* <SoftInput
            type="password"
            name="password"
            placeholder="Enter your password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          /> */}
          <SoftInput
            type={showPassword ? "text" : "password"}
            name="password"
            placeholder="Enter your password"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              setPasswordError(false);
            }}
            error={passwordError}
            icon={{
              component: (
                <IconButton type="button" onClick={handleTogglePassword} size="small" tabIndex={-1}>
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              ),
              direction: "right",
            }}
          />
        </SoftBox>

        <SoftBox display="flex" alignItems="center">
          <Switch checked={rememberMe} onChange={handleSetRememberMe} />
          <SoftTypography
            variant="button"
            fontWeight="regular"
            onClick={handleSetRememberMe}
            sx={{ cursor: "pointer", userSelect: "none" }}
          >
            &nbsp;&nbsp;Remember me
          </SoftTypography>
        </SoftBox>

        <SoftBox mt={4} mb={1}>
          <SoftButton
            variant="gradient"
            color="info"
            component="button"
            type="submit"
            fullWidth
            disabled={isSubmitDisabled}
          >
            {isLoading ? "Signing in..." : "Sign In"}
          </SoftButton>
        </SoftBox>

        <SoftBox mt={3} textAlign="center">
          <SoftTypography variant="button" color="text" fontWeight="regular">
            Don&apos;t have an account?{" "}
            <SoftTypography
              component={Link}
              to="/sign-up"
              variant="button"
              color="info"
              fontWeight="medium"
              textGradient
            >
              Sign up
            </SoftTypography>
          </SoftTypography>
        </SoftBox>

        <SoftBox mt={2} textAlign="center">
          <SoftTypography
            component={Link}
            to="/forget-password"
            variant="button"
            color="info"
            fontWeight="medium"
            textGradient
          >
            Forgot Password?
          </SoftTypography>
        </SoftBox>
      </SoftBox>
    </CoverLayout>
  );
}

export default SignIn;